import React, { useState } from 'react';
import { usePolicy } from '../context/PolicyContext';
import './Form.css';

const AddIncident: React.FC = () => {
  const { getPolicy, addIncident } = usePolicy();
  const [policyNumber, setPolicyNumber] = useState('');
  const [incidentDate, setIncidentDate] = useState('');
  const [description, setDescription] = useState('');
  const [claimAmount, setClaimAmount] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [policyFound, setPolicyFound] = useState(false);
  const [policyDetails, setPolicyDetails] = useState<any>(null);

  const handlePolicyCheck = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setPolicyFound(false);
    setPolicyDetails(null);

    if (!policyNumber.trim()) {
      setErrors({ policyNumber: 'Please enter a policy number' });
      return;
    }

    const policy = getPolicy(policyNumber.trim());
    if (policy) {
      if (policy.status === 'cancelled') {
        setErrors({ policyNumber: 'Cannot add incidents to cancelled policies' });
        return;
      }
      setPolicyFound(true);
      setPolicyDetails(policy);
      setIncidentDate(new Date().toISOString().split('T')[0]);
    } else {
      setErrors({ policyNumber: 'Policy not found' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!incidentDate) {
      newErrors.incidentDate = 'Incident date is required';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!claimAmount || parseFloat(claimAmount) <= 0) {
      newErrors.claimAmount = 'Claim amount must be greater than 0';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const incident = {
      id: Date.now().toString(),
      incidentDate,
      description: description.trim(),
      claimAmount: parseFloat(claimAmount),
      status: 'pending' as const,
      createdAt: new Date().toISOString(),
    };

    addIncident(policyNumber.trim(), incident);
    setSuccess(true);
    setPolicyNumber('');
    setIncidentDate('');
    setDescription('');
    setClaimAmount('');
    setPolicyFound(false);
    setPolicyDetails(null);

    setTimeout(() => setSuccess(false), 5000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="form-container">
      <h2>Add Incident/Claim to Policy</h2>

      {success && (
        <div className="success-message">
          Incident has been added successfully! The policy coverage amount has been increased.
        </div>
      )}

      {!policyFound ? (
        <form onSubmit={handlePolicyCheck} className="form">
          <div className="form-group">
            <label htmlFor="policyNumber">Policy Number *</label>
            <input
              type="text"
              id="policyNumber"
              name="policyNumber"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              placeholder="Enter policy number"
              className={errors.policyNumber ? 'error' : ''}
            />
            {errors.policyNumber && <span className="error-text">{errors.policyNumber}</span>}
          </div>

          <button type="submit" className="submit-button">Verify Policy</button>
        </form>
      ) : (
        <div>
          {policyDetails && (
            <div className="policy-preview">
              <h3>Policy Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <strong>Policy Number:</strong>
                  <span>{policyDetails.policyNumber}</span>
                </div>
                <div className="detail-item">
                  <strong>Policy Holder:</strong>
                  <span>{policyDetails.policyHolderName}</span>
                </div>
                <div className="detail-item">
                  <strong>Current Coverage:</strong>
                  <span>${policyDetails.coverageAmount.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <strong>Policy Type:</strong>
                  <span>{policyDetails.policyType}</span>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label htmlFor="policyNumber">Policy Number</label>
              <input
                type="text"
                id="policyNumber"
                name="policyNumber"
                value={policyNumber}
                disabled
                className="disabled-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="incidentDate">Incident Date *</label>
              <input
                type="date"
                id="incidentDate"
                name="incidentDate"
                value={incidentDate}
                onChange={(e) => {
                  setIncidentDate(e.target.value);
                  if (errors.incidentDate) {
                    setErrors({ ...errors, incidentDate: '' });
                  }
                }}
                max={new Date().toISOString().split('T')[0]}
                className={errors.incidentDate ? 'error' : ''}
              />
              {errors.incidentDate && <span className="error-text">{errors.incidentDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Incident Description *</label>
              <textarea
                id="description"
                name="description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors({ ...errors, description: '' });
                  }
                }}
                rows={5}
                placeholder="Describe the incident or claim in detail..."
                className={errors.description ? 'error' : ''}
              />
              {errors.description && <span className="error-text">{errors.description}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="claimAmount">Claim Amount ($) *</label>
              <input
                type="number"
                id="claimAmount"
                name="claimAmount"
                value={claimAmount}
                onChange={(e) => {
                  setClaimAmount(e.target.value);
                  if (errors.claimAmount) {
                    setErrors({ ...errors, claimAmount: '' });
                  }
                }}
                min="0"
                step="0.01"
                placeholder="Enter the claim amount"
                className={errors.claimAmount ? 'error' : ''}
              />
              {errors.claimAmount && <span className="error-text">{errors.claimAmount}</span>}
              <small className="form-hint">
                This amount will be added to the policy's coverage amount.
              </small>
            </div>

            <div className="info-box">
              <p>
                <strong>Note:</strong> Adding an incident will increase the policy's coverage amount 
                by the claim amount specified. The incident will be marked as "pending" for review.
              </p>
            </div>

            <div className="button-group">
              <button type="submit" className="submit-button">Add Incident</button>
              <button
                type="button"
                className="submit-button secondary"
                onClick={() => {
                  setPolicyFound(false);
                  setPolicyNumber('');
                  setIncidentDate('');
                  setDescription('');
                  setClaimAmount('');
                  setPolicyDetails(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddIncident;


